import React from "react";
import { canEstimateRidePrice, formatRidePrice } from "../rideOrderPayload";
import { DEFAULT_ORDER_FORM_LOCALE } from "../orderFormLocale";
import { OrderFormLocale, OrderFormState, RideType } from "../types";
import { PriceEstimateState } from "./useOrderPriceEstimate";
import styles from "./orderForm.module.scss";

type OrderPriceEstimateProps = {
  form: OrderFormState;
  rideType: RideType;
  estimate: PriceEstimateState;
  locale?: OrderFormLocale;
};

const OrderPriceEstimate: React.FC<OrderPriceEstimateProps> = ({
  form,
  rideType,
  estimate,
  locale,
}) => {
  const resolvedLocale = locale ?? DEFAULT_ORDER_FORM_LOCALE;
  const priceLabels = resolvedLocale.price ?? DEFAULT_ORDER_FORM_LOCALE.price;

  let content: React.ReactNode;

  if (!canEstimateRidePrice(form, rideType)) {
    content = (
      <span className={styles.priceEstimateHint}>
        {priceLabels?.unavailable ?? "Complete route and vehicle to see price"}
      </span>
    );
  } else if (estimate.status === "loading") {
    content = (
      <span className={styles.priceEstimateHint}>
        {priceLabels?.loading ?? "Calculating price…"}
      </span>
    );
  } else if (estimate.status === "error") {
    content = <span className={styles.priceEstimateError}>{estimate.message}</span>;
  } else if (estimate.status === "ready" && estimate.price != null) {
    content = <span className={styles.priceEstimateValue}>{formatRidePrice(estimate.price)}</span>;
  } else {
    content = (
      <span className={styles.priceEstimateHint}>
        {priceLabels?.unavailable ?? "Complete route and vehicle to see price"}
      </span>
    );
  }

  return (
    <div className={styles.priceEstimateWrap} aria-live="polite">
      <span className={styles.priceEstimateLabel}>{priceLabels?.label ?? "Estimated price"}</span>
      {content}
    </div>
  );
};

export default OrderPriceEstimate;
